import React, { useState } from "react";
import axios from "axios";
import "../../Style/TableStyle.scss";
import "../../Style/__searchbar.scss";
import "../../Style/__button.scss";
import "./SuppliersPage.scss";
import {
  deleteAlert,
  errorAlert,
  handleSuccess,
} from "../../Components/Alerts";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { MdOutlinePeopleAlt } from "react-icons/md";
import DataGridServerSide from "../../Components/DataGridServerSide";
import SuppliersModal from "./SuppliersModal";
import SupplierViewModal from "./SupplierViewModal";
import CrudPageHeader from "../../Components/CrudPageHeader";
import useAccountsGridData from "../../hooks/useAccountsGridData";

export default function SuppliersPage() {
  const headers = authHeader();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [viewSupplier, setViewSupplier] = useState(null);

  const getApiErrorMessage = (error, fallbackMessage) => {
    if (error?.response?.data?.errorMessage) {
      return `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`;
    }

    if (
      typeof error?.response?.data === "string" &&
      error.response.data.trim() !== ""
    ) {
      return error.response.data;
    }

    if (error?.message) {
      return `Gabim: ${error.message}`;
    }

    return fallbackMessage;
  };

  const {
    loading,
    data,
    rowCount,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    quickFilter,
    setQuickFilter,
    fetchData,
  } = useAccountsGridData({
    headers,
    ledgerID: 302,
    onError: (error) =>
      errorAlert(
        getApiErrorMessage(error, "Gabim i panjohur në marrjen e furnitorëve."),
      ),
  });

  const handleEdit = async (item) => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + `Accounts/getAccountForEdit/${item.AccountID}`,
        { headers },
      );

      setSelectedSupplier(response.data);
      setShowEditModal(true);
    } catch (error) {
      errorAlert(
        getApiErrorMessage(
          error,
          "Gabim në marrjen e të dhënave të furnitorit për editim.",
        ),
      );
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedSupplier(null);
  };

  const handleView = async (item) => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + `Accounts/getAccountForEdit/${item.AccountID}`,
        { headers },
      );

      setViewSupplier(response.data);
      setShowViewModal(true);
    } catch (error) {
      errorAlert(
        getApiErrorMessage(
          error,
          "Gabim në marrjen e të dhënave të furnitorit për shikim.",
        ),
      );
    }
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewSupplier(null);
  };

  const handleDelete = async (deleteItemId) => {
    try {
      const response = await axios.delete(
        ApiLink.API_URL + "Accounts" + `/${deleteItemId}`,
        {
          headers,
        },
      );

      const result = response.data;
      if (result.success) {
        handleSuccess("Furnitori është fshirë me sukses");
        fetchData();
      } else {
        deleteAlert(
          "Furnitori nuk mund të fshihet sepse është përdorur në transaksione",
        );
      }
    } catch (error) {
      deleteAlert("Ka ndodhur një gabim gjatë fshirjes së furnitorit.");
    }
  };

  const suppliersGridHeader = [
    {
      field: "id",
      headerName: "#",
      width: 50,
      headerClassName: "super-app-theme--header",
      sortable: false,
      filterable: false,
      groupable: false,
      aggregable: false,
    },
    {
      field: "AccountName",
      headerName: "Emri i Furnitorit",
      flex: 2.5,
      minWidth: 260,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "IdentificationNumber",
      headerName: "Numri Identifikues",
      flex: 1.2,
      minWidth: 170,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "VATNumber",
      headerName: "Numri i TVSH-së",
      flex: 1.2,
      minWidth: 170,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "PhoneNumbers",
      headerName: "Numri Telefonit",
      flex: 1.2,
      minWidth: 170,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "Adress",
      headerName: "Adresa",
      flex: 1.5,
      minWidth: 200,
      editable: false,
      headerClassName: "super-app-theme--header",
      groupable: false,
      aggregable: false,
    },
    {
      field: "IsActive",
      headerName: "Aktiv",
      width: 70,
      headerClassName: "super-app-theme--header",
      renderCell: (params) =>
        params.value ? (
          <CheckCircleOutlineIcon style={{ color: "green" }} />
        ) : (
          ""
        ),
    },
  ];

  const rows = data.map((item, index) => ({
    id: paginationModel.page * paginationModel.pageSize + index + 1,
    AccountName: item.AccountName,
    IdentificationNumber: item.IdentificationNumber,
    VATNumber: item.VATNumber,
    PhoneNumbers: item.PhoneNumbers ?? item.Phone ?? "",
    Adress: item.Adress ?? "",
    IsActive: Boolean(item.IsActive),
    deleteItemId: item.AccountID,
    rowInfo: item,
  }));

  return (
    <div className="Crud__Container SUP__page">
      <SuppliersModal
        show={showEditModal}
        onHide={handleCloseEditModal}
        mode="edit"
        initialData={selectedSupplier}
        fetchData={fetchData}
        type="none"
      />

      <SupplierViewModal
        show={showViewModal}
        onHide={handleCloseViewModal}
        supplierData={viewSupplier}
      />

      <CrudPageHeader
        icon={MdOutlinePeopleAlt}
        title="Lista e Furnitoreve të rinj"
        description="Forma e Furnitorëve: Menaxhoni, shtoni, editoni dhe fshini Furnitorë."
        tutorialUrl="https://www.youtube.com/@Accountree"
        tutorialLabel="Shiko Tutorialin"
        action={
          <SuppliersModal fetchData={fetchData} type="text" mode="insert" />
        }
        className="SUP__header"
      />

      <DataGridServerSide
        columns={suppliersGridHeader}
        rows={rows}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        handleShowPrint={handleView}
        showPayment={false}
        height={500}
        rowCount={rowCount}
        loading={loading}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        sortModel={sortModel}
        setSortModel={setSortModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        showDetailsStatus={true}
      />
    </div>
  );
}
